import {
  ApplicationResult,
  ApplyJobParams,
  ContractType,
  EmploymentType,
  Job,
  JobApplication,
  JobApplicationStatus,
  JobsResponse,
  JOBS_API_TIMEOUT_MS,
} from '@cibli/shared';

import api from './api';

/**
 * Job search based on a CV.
 * Returns a Job[] array (no pagination).
 */
export const getJobs = async (
  cvId: string,
  boothId?: string,
  search?: string,
  city?: string,
  contractTypes?: ContractType[],
  employmentTypes?: EmploymentType[],
): Promise<JobsResponse> => {
  return await api.post<
    JobsResponse,
    {
      cvId: string;
      boothId?: string;
      search?: string;
      city?: string;
      contractTypes?: ContractType[];
      employmentTypes?: EmploymentType[];
    }
  >(
    '/jobs',
    {
      cvId,
      ...(boothId && { boothId }),
      ...(search && search.trim() && { search: search.trim() }),
      ...(city && city.trim() && { city: city.trim() }),
      ...(contractTypes && contractTypes.length > 0 && { contractTypes }),
      ...(employmentTypes && employmentTypes.length > 0 && { employmentTypes }),
    },
    { timeout: JOBS_API_TIMEOUT_MS },
  );
};

/**
 * Browse all available jobs (no CV required).
 * Uses GET /jobs endpoint — returns all jobs with optional filters.
 */
export const browseJobs = async (search?: string): Promise<JobsResponse> => {
  const params = new URLSearchParams();
  if (search && search.trim()) {
    params.set('motsCles', search.trim());
  }
  const query = params.toString();
  return await api.get<JobsResponse>(`/jobs${query ? `?${query}` : ''}`, {
    timeout: JOBS_API_TIMEOUT_MS,
  });
};

export const applyToJob = async (job: Job, cvId: string): Promise<ApplicationResult> => {
  return await api.post<ApplicationResult, ApplyJobParams>('/jobs/apply', {
    job,
    cvId,
  });
};

export const beContacted = async (): Promise<void> => {
  return await api.post<void, undefined>('/jobs/be-contacted', undefined);
};

/**
 * Get all job applications for the authenticated user.
 * Returns list of jobs the user has already applied to.
 */
export const getUserJobApplications = async (signal?: AbortSignal): Promise<JobApplication[]> => {
  return await api.get<JobApplication[]>('/jobs/applications', signal ? { signal } : undefined);
};

export const getJobById = async (id: string): Promise<Job | null> => {
  return await api.get<Job>(`/jobs/${encodeURIComponent(id)}`);
};

export const updateApplicationStatus = async (
  applicationId: string,
  status: JobApplicationStatus,
): Promise<JobApplication> => {
  return await api.patch<JobApplication, { status: JobApplicationStatus }>(
    `/jobs/applications/${applicationId}/status`,
    { status },
  );
};
